import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Listen for slideshow select events to sync mobile thumbnails
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect.bind(this));

    // Listen for variant update events to update media gallery
    // Use closest section scope like other product components
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }

    // Initialize mobile thumbnails on load
    this.#initializeMobileThumbnails();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up event listeners
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  /**
   * Handle slideshow select events
   * @param {CustomEvent} event - The slideshow select event
   */
  #handleSlideshowSelect(event) {
    const currentIndex = event.detail?.index ?? 0;
    this.#syncMobileThumbnails(currentIndex);
  }

  /**
   * Initialize mobile thumbnails on component load
   */
  #initializeMobileThumbnails() {
    // Get current slideshow state
    const currentIndex = this.slideshow?.current || 0;
    
    // Sync mobile thumbnails to match initial state
    this.#syncMobileThumbnails(currentIndex);
  }

  /**
   * Synchronizes mobile thumbnail aria-selected states with the current slide.
   * @param {number} currentIndex - The index of the currently selected slide.
   */
  #syncMobileThumbnails(currentIndex) {
    // Find all mobile thumbnails
    const mobileThumbnails = this.querySelectorAll('.media-gallery__mobile-controls .slideshow-control');
    
    // Update aria-selected for all mobile thumbnails
    mobileThumbnails.forEach((thumb, index) => {
      const isSelected = index === currentIndex;
      thumb.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      
      // Scroll the selected thumbnail into view for better UX
      if (isSelected) {
        thumb.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'center' 
        });
      }
    });
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Enhanced animation sequence
    this.#animateGalleryTransition(newMediaGallery);
  };

  /**
   * Animates the transition between gallery variants with smooth fade effect
   * @param {HTMLElement} newMediaGallery - The new gallery element to replace with
   */
  #animateGalleryTransition(newMediaGallery) {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Simple fade for reduced motion
      this.style.transition = 'opacity 0.15s ease-out';
      this.style.opacity = '0';
      
      setTimeout(() => {
        this.replaceWith(newMediaGallery);
      }, 150);
      return;
    }

    // Add loading state
    this.setAttribute('data-transitioning', 'true');
    
    // Set up initial transition styles with hardware acceleration
    this.style.transition = 'opacity 0.25s ease-out, transform 0.25s ease-out, filter 0.15s ease-out';
    this.style.transformOrigin = 'center center';
    this.style.willChange = 'opacity, transform, filter';
    
    // Phase 1: Fade out current gallery with subtle scale and blur
    this.style.opacity = '0';
    this.style.transform = 'scale(0.98)';
    
    // Phase 2: Replace gallery after fade out completes
    setTimeout(() => {
      // Prepare new gallery for fade in
      newMediaGallery.style.opacity = '0';
      newMediaGallery.style.transform = 'scale(1.02)';
      newMediaGallery.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
      newMediaGallery.style.transformOrigin = 'center center';
      newMediaGallery.style.willChange = 'opacity, transform';
      
      // Replace the DOM element
      this.replaceWith(newMediaGallery);
      
      // Phase 3: Fade in new gallery with smooth entrance
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { // Double RAF for smoother animation
          newMediaGallery.style.opacity = '1';
          newMediaGallery.style.transform = 'scale(1)';
          
          // Clean up inline styles after animation completes
          setTimeout(() => {
            newMediaGallery.style.opacity = '';
            newMediaGallery.style.transform = '';
            newMediaGallery.style.transition = '';
            newMediaGallery.style.transformOrigin = '';
            newMediaGallery.style.willChange = '';
            newMediaGallery.removeAttribute('data-transitioning');
          }, 300);
        });
      });
    }, 250);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    console.log('🔍 MediaGallery: ZoomMediaSelectedEvent received:', {
      index: event.detail.index
    });
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    console.log('🔍 MediaGallery: Zoom requested for index:', index);
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
